
import { useQuery } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { ProcessingStatus } from '@/components/products/types/processing';
import { useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { debounce } from "lodash";
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js";

export const useProcessingStatus = (collectionId: string) => {
  const queryClient = useQueryClient();

  // Debounced invalidation to prevent rapid re-renders
  const debouncedInvalidate = useCallback(
    debounce(() => {
      queryClient.invalidateQueries({
        queryKey: ['processing-status', collectionId]
      });
    }, 1000),
    [collectionId, queryClient]
  );

  useEffect(() => {
    // Set up realtime subscription for the progress_tracking table
    const channel = supabase
      .channel(`processing-status-${collectionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'progress_tracking',
          filter: `collection_id=eq.${collectionId}` // Listen for all progress for this collection
        },
        (payload: RealtimePostgresChangesPayload<ProcessingStatus>) => {
          console.log('Received progress update:', payload);
          
          // Type guard to ensure we have the new record
          if (!payload.new || typeof payload.new !== 'object') {
            return;
          }

          // Type guard for old record comparison
          const newRecord = payload.new as ProcessingStatus;
          const oldRecord = payload.old ? (payload.old as ProcessingStatus) : null;

          // Only invalidate if we have meaningful changes that should trigger a UI update
          const hasProgressChanged = newRecord.processed_items !== oldRecord?.processed_items;
          const hasStatusChanged = newRecord.status !== oldRecord?.status;
          const hasStageChanged = newRecord.current_stage !== oldRecord?.current_stage;
          const hasLogsChanged = JSON.stringify(newRecord.log_messages) !== JSON.stringify(oldRecord?.log_messages);
          
          if (hasProgressChanged || hasStatusChanged || hasStageChanged || hasLogsChanged) {
            console.log('Invalidating query due to meaningful changes:', {
              oldProgress: oldRecord?.processed_items,
              newProgress: newRecord.processed_items,
              oldStatus: oldRecord?.status,
              newStatus: newRecord.status,
              oldStage: oldRecord?.current_stage,
              newStage: newRecord.current_stage,
              hasLogsChanged
            });
            
            debouncedInvalidate();
          }
        }
      )
      .subscribe();

    return () => {
      console.log('Cleaning up processing status subscription');
      debouncedInvalidate.cancel();
      supabase.removeChannel(channel);
    };
  }, [collectionId, queryClient, debouncedInvalidate]);

  return useQuery({
    queryKey: ['processing-status', collectionId],
    queryFn: async () => {
      console.log('Fetching processing status for collection:', collectionId);
      
      // Fetch progress_tracking data
      const { data: progressData, error: progressError } = await supabase
        .from('progress_tracking')
        .select('*')
        .eq('id', `upload-${collectionId}`)
        .maybeSingle();

      if (progressError) {
        console.error('Error fetching processing status:', progressError);
        throw progressError;
      }

      // If we have progress data, validate it against actual product counts
      if (progressData) {
        // Get actual product counts to validate progress accuracy
        const { data: products } = await supabase
          .from('product_descriptions')
          .select('processing_status, status')
          .eq('collection_id', collectionId);

        if (products) {
          const totalProducts = products.length;
          const completedProducts = products.filter(p => 
            p.processing_status === 'completed' || p.status === 'approved'
          ).length;

          // If progress_tracking shows 0 processed but all products are actually completed,
          // this indicates stale data - return null to let StatusBadges show correct state
          if (progressData.processed_items === 0 && completedProducts === totalProducts && totalProducts > 0) {
            console.log('Detected stale progress_tracking data - returning null');
            return null;
          }
        }
      }

      return progressData as ProcessingStatus | null;
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      // Continue refetching while processing
      if (!data || data.status === 'processing') {
        return 1000;
      }
      return false;  // Stop refetching once completed or if no data
    },
    staleTime: 0,  // Consider data immediately stale to enable refetching
    gcTime: 300000  // Cache completed states for 5 minutes
  });
};
